import classnames from "classnames";
import React from "react";
import _ from "underscore";
import { LazyLoadImage } from "../../../../core/atoms/atoms";
import { isBrowser } from "../../../../core/detection";

import "./imageCycler.scss";


interface ImageCyclerImage {
  image: IResponsiveImage,
  meta: string,
}

interface Props {
  images: ImageCyclerImage[];
  cycleInterval?: number;
}

type State = Partial<{
  activeImageIndex: number;
  imageOneIndex: number;
  imageTwoIndex: number;
}>;

/**
 * Component to calmly cycle through a selection of images when unattended
 */
export class ImageCycler extends React.Component<Props, State> {
  private transitionInterval: number;
  private updateInProgress: boolean;

  public constructor(props: Props) {
    super(props);
    this.state = {
      activeImageIndex: 0,
      imageOneIndex: 0,
      imageTwoIndex: 1,
    };

    if (isBrowser()) {
      this.setTransitionInterval();
    }
  }

  private cycleForward = () => {
    if (this.updateInProgress) {
      return;
    }

    this.updateInProgress = true;

    // stop cycling interval to avoid conflicts with state updates
    window.clearInterval(this.transitionInterval);

    const imageToSetVisible = this.state.activeImageIndex === this.state.imageOneIndex ? 1 : 0;
    const imageIndexState = imageToSetVisible === 1 ? {
      imageOneIndex: (this.state.imageTwoIndex + 1) % this.props.images.length,
    } : {
      imageTwoIndex: (this.state.imageOneIndex + 1) % this.props.images.length,
    };

    this.setState({
      activeImageIndex: imageToSetVisible === 1 ? this.state.imageTwoIndex : this.state.imageOneIndex,
    });

    window.setTimeout(() => {
      this.setState(imageIndexState, () => {
        this.updateInProgress = false;

        // restart cycle from the point this transition has ended
        this.setTransitionInterval();
      });
    }, 300);
  }

  private setTransitionInterval = () => {
    if (this.props.images.length <= 1) {
      return;
    }

    this.transitionInterval = window.setInterval(this.cycleForward, this.props.cycleInterval || 15000);
  }

  public render() {
    const { images } = this.props;
    const { imageOneIndex, imageTwoIndex, activeImageIndex } = this.state;

    if (!images) {
      return;
    }

    return (
      <div className="image-cycler">
        {/*
          * To circumvent the LazyLoadImage only rendering an image on screen, place 2 images on top of each other
          * and handle transitions between them using opacity. Falls back to a single image display if there
          * is only 1 image provided
          */}
        <div className={classnames("fade-image-wrapper", { visible: imageOneIndex === activeImageIndex })}>
          <LazyLoadImage key={imageOneIndex} src={images[imageOneIndex].image} alt={images[imageOneIndex].meta} />
        </div>

        {images.length > 1 && (
          <div className={classnames("fade-image-wrapper", { visible: imageTwoIndex === activeImageIndex })}>
            <LazyLoadImage key={imageTwoIndex} src={images[imageTwoIndex].image} alt={images[imageTwoIndex].meta} />
          </div>
        )}

        {images.length > 1 && (
          <div className="pips" onClick={this.cycleForward}>
            {_.range(0, images.length).map(index => (
              <div key={index} className={classnames("pip", { active: index === activeImageIndex })} />
            ))}
          </div>
        )}
      </div>
    );
  }
}
